import { CalendarCheck, ShieldCheck, Sparkles, SquareCheckBig } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { Logo } from "../components/Logo";
import { ErrorNote } from "../components/ui";
import { ACCOUNT_DELETED_KEY, APP_NAME } from "../config";

const FEATURES = [
  { icon: CalendarCheck, title: "Everything in one place", text: "Assignments, tests and reminders from every class." },
  { icon: Sparkles, title: "Catches hidden homework", text: "Finds the quiz your teacher only mentioned in a post." },
  { icon: SquareCheckBig, title: "Daily checklist", text: "See what's due today and this week at a glance." },
];

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

export function LandingPage() {
  const { signInWithGoogle } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set by Settings → Delete my account, shown once.
  const [deleted] = useState(() => {
    try {
      const was = sessionStorage.getItem(ACCOUNT_DELETED_KEY) === "1";
      sessionStorage.removeItem(ACCOUNT_DELETED_KEY);
      return was;
    } catch {
      return false;
    }
  });

  const signIn = async () => {
    setError(null);
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code !== "auth/popup-closed-by-user" && code !== "auth/cancelled-popup-request") {
        setError("We couldn't sign you in with Google. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="mx-auto w-full max-w-5xl px-4 py-4">
        <Logo />
      </header>
      {deleted && (
        <p role="status" className="mx-auto w-full max-w-5xl px-4">
          <span className="block rounded-xl bg-green-50 p-3 text-sm font-medium text-green-900">
            Your account and everything in it were deleted. Thanks for trying {APP_NAME}.
          </span>
        </p>
      )}

      <main className="mx-auto grid w-full max-w-5xl flex-1 items-center gap-10 px-4 pb-6 md:grid-cols-2">
        <div>
          <h1 className="text-3xl font-extrabold leading-tight text-ink md:text-5xl">
            Make school <em className="whitespace-nowrap">Due-able.</em> <span className="text-brand">The Best Student Organizer.</span>
          </h1>
          <p className="mt-3 text-lg text-ink-muted">
            {APP_NAME} converts your teachers' posts and assignments from Google Classroom into one simple checklist, with dates.
          </p>
          <p className="mt-2 text-lg font-semibold text-ink">It finds what's due so you don't have to.</p>

          <button
            type="button"
            onClick={signIn}
            disabled={busy}
            className="mt-6 flex min-h-12 w-full items-center justify-center gap-3 rounded-xl bg-brand px-6 text-base font-semibold text-white shadow-md hover:bg-brand-strong disabled:opacity-60 sm:w-auto"
          >
            <span className="rounded-md bg-white p-1">
              <GoogleMark />
            </span>
            {busy ? "Signing in…" : "Continue with Google"}
          </button>
          {error && (
            <div className="mt-3">
              <ErrorNote message={error} />
            </div>
          )}
          <p className="mt-3 text-sm text-ink-muted">
            School account says "Access blocked"?{" "}
            <Link to="/schools" className="font-semibold text-brand underline">
              Here's what your school's admin needs to do
            </Link>
          </p>

          <ul className="mt-6 space-y-3">
            {FEATURES.map((f) => (
              <li key={f.title} className="flex gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand">
                  <f.icon size={20} aria-hidden />
                </span>
                <span>
                  <span className="block font-semibold text-ink">{f.title}</span>
                  <span className="block text-sm text-ink-muted">{f.text}</span>
                </span>
              </li>
            ))}
          </ul>

          <p className="mt-6 flex items-start gap-2 rounded-xl bg-surface p-3 text-sm text-ink-muted ring-1 ring-line">
            <ShieldCheck size={20} className="shrink-0 text-success" aria-hidden />
            <span>
              No ads. We never sell your data. Names are removed before we compile your class information. Our AI sees only
              your schoolwork, never your personal information.{" "}
              <Link to="/privacy" className="font-semibold text-brand underline">
                How we protect your data
              </Link>
            </span>
          </p>
        </div>

        <PreviewCard />
      </main>

      <footer className="border-t border-line bg-surface">
        <nav aria-label="Footer" className="mx-auto flex max-w-5xl flex-wrap gap-x-6 px-4 text-sm text-ink-muted">
          <Link to="/privacy" className="flex min-h-11 items-center hover:text-ink">
            Privacy &amp; NY Ed Law 2-d
          </Link>
          <Link to="/terms" className="flex min-h-11 items-center hover:text-ink">
            Terms
          </Link>
          <Link to="/schools" className="flex min-h-11 items-center hover:text-ink">
            For schools
          </Link>
          <Link to="/contact" className="flex min-h-11 items-center hover:text-ink">
            Contact
          </Link>
          <p className="flex min-h-11 items-center sm:ml-auto">© 2026 Built by Techminded Inc.</p>
        </nav>
      </footer>
    </div>
  );
}

/** Illustration of the Checklist. Hidden on phones so the page fits one screen. */
function PreviewCard() {
  const rows = [
    { title: "Worksheet 3.2", tag: "Math 8", color: "bg-blue-600", time: "Today" },
    { title: "Bring signed lab contract", tag: "Science 8", color: "bg-emerald-600", time: "Wed" },
    { title: "Chapter 5 vocab quiz", tag: "Social Studies", color: "bg-amber-600", time: "Fri" },
  ];
  return (
    <div aria-hidden className="hidden rounded-3xl bg-gradient-to-br from-brand to-blue-400 p-6 shadow-xl md:block">
      <div className="rounded-2xl bg-surface p-5">
        <p className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Today</p>
        <ul className="mt-3 space-y-3">
          {rows.map((r) => (
            <li key={r.title} className="flex items-center gap-3 rounded-xl border border-line p-3">
              <span className="h-5 w-5 rounded-md border-2 border-slate-300" />
              <span className="flex-1">
                <span className="block font-semibold text-ink">{r.title}</span>
                <span className="flex items-center gap-1.5 text-xs text-ink-muted">
                  <span className={`h-2 w-2 rounded-full ${r.color}`} />
                  {r.tag}
                </span>
              </span>
              <span className="text-sm font-medium text-ink-muted">{r.time}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 rounded-xl bg-brand-soft px-3 py-2 text-sm text-brand-strong">
          ✨ Found in a teacher post: <strong>Chapter 5 vocab quiz, Friday</strong>
        </p>
      </div>
    </div>
  );
}
